/**
 * 主题偏好（浅色 / 深色 / 跟随系统）。
 *
 * ## 为什么是模块级 store，而不是 useState / Context
 *
 * 有两个消费点在 React 之外：
 *   1. 首帧之前要把 `dark` 类打到 `<html>` 上（管理端在 index.html 的内联脚本里做，
 *      桌面端在 main.tsx 顶部调 initTheme()）；
 *   2. `main.tsx` 里与 <App> 平级的 sonner `<Toaster theme=…>` 需要同一份状态。
 * 订阅写法对齐 lib/api.ts 里 onSessionExpired 的既有约定，不引入额外状态库。
 *
 * ## 与另一端的重复
 *
 * 本文件与 desktop/src/lib/theme.ts 是同一套实现，**刻意重复**，仅三处不同：
 * 存储键、是否更新 `theme-color` meta、是否有跨标签页监听。改动请同步两端。
 *
 * ⚠️ 还有第三处副本：admin/index.html 里的首帧内联脚本（无法 import 模块），
 *    只实现了 readPref/resolve/apply 三条逻辑，改键名或默认值时务必同步。
 */
import { useSyncExternalStore } from 'react'

export type ThemePref = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

/** 存储键：沿用 lib/api.ts 的 `dskgi-admin-<thing>` 命名约定。 */
const KEY = 'dskgi-admin-theme'
const QUERY = '(prefers-color-scheme: dark)'

interface Snapshot {
  pref: ThemePref
  /** 实际生效的主题（pref 为 system 时由系统决定）。 */
  resolved: ResolvedTheme
}

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
  } catch {
    // 隐私模式 / 禁用存储：内存态照常工作
    return 'system'
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(QUERY).matches
  )
}

function resolve(pref: ThemePref): ResolvedTheme {
  return pref === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : pref
}

let snapshot: Snapshot = (() => {
  const pref = readPref()
  return { pref, resolved: resolve(pref) }
})()

const listeners = new Set<() => void>()

/** 把主题落到 DOM：只切 <html> 上的一个类；color-scheme 由 index.css 的 :root/.dark 负责。 */
function apply() {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', snapshot.resolved === 'dark')
  // 浏览器/已安装 PWA 的标题栏颜色。深色保持 index.html 里写死的 #0f1117 不变。
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', snapshot.resolved === 'dark' ? '#0f1117' : '#f9fafb')
}

function commit(next: Snapshot) {
  snapshot = next
  apply()
  listeners.forEach((l) => l())
}

let started = false

/** 幂等；在 main.tsx 顶部调用一次。 */
export function initTheme(): void {
  if (started) return
  started = true
  apply()
  if (typeof window === 'undefined') return
  // 跟随系统：系统外观变化时实时切换
  window.matchMedia?.(QUERY).addEventListener?.('change', () => {
    if (snapshot.pref === 'system') commit({ pref: 'system', resolved: resolve('system') })
  })
  // 跨标签页：一个标签切了主题，另一个跟着变
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return
    const pref = readPref()
    commit({ pref, resolved: resolve(pref) })
  })
}

/** 设置偏好并持久化。 */
export function setTheme(next: ThemePref): void {
  try {
    localStorage.setItem(KEY, next)
  } catch {
    // 存不下也要在当前会话生效
  }
  commit({ pref: next, resolved: resolve(next) })
}

/**
 * 服务端渲染快照。
 *
 * 必须是**模块级常量**：useSyncExternalStore 会比对返回值，返回新对象会抛
 * 「The result of getServerSnapshot should be cached」。取深色以与 index.html 的
 * `class="dark"` 兜底一致。
 */
const SERVER_SNAPSHOT: Snapshot = { pref: 'system', resolved: 'dark' }

export function useTheme(): Snapshot & { setTheme: typeof setTheme } {
  const snap = useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => snapshot,
    () => SERVER_SNAPSHOT,
  )
  return { ...snap, setTheme }
}

/** 三态选项（供 ThemeToggle 渲染；label 同时用于 title/sr-only）。 */
export const THEME_OPTIONS: { value: ThemePref; label: string }[] = [
  { value: 'light', label: '浅色' },
  { value: 'dark', label: '深色' },
  { value: 'system', label: '跟随系统' },
]
